import Promise from 'bluebird';
import _ from 'underscore';

import { StringPayload } from '@bingads-webui/azure-upload';

import { CommonImportRepository } from '../common-repository';
import { getLocalizedCampaignTypeStringUtil, containDisplayTypeUtil, filterAndLocalizeCampaignsUtil } from '../import-repository-utils';

const BASE_10_RADIX = 10;
const GOOGLE_LOGOUT_URL = 'https://www.google.com/accounts/Logout';

const getGoogleSessionUrlTmpl =
  'ImportSession/StartGoogleSession(CustomerId=:customer_id,AccountId=:account_id)?authCode={{AuthCode}}&redirectUri=postmessage';
const getGoogleSessionWithTokenUrlTmpl =
  'ImportSession/StartGoogleSession(CustomerId=:customer_id,AccountId=:account_id)?refreshToken={{Token}}&redirectUri=postmessage';
const getGoogleLoginEmailUrlTmpl =
  'ImportSession/GoogleLoginEmail(CustomerId=:customer_id,AccountId=:account_id,SessionId={{SessionId}})';
const getGoogleAccountsUrlTmpl =
  '/ImportSession/GoogleAccounts(CustomerId=:customer_id,AccountId=:account_id,SessionId={{SessionId}})';
const getGoogleCampaignsUrlTmpl =
  '/ImportSession/GoogleCampaigns(CustomerId=:customer_id,AccountId=:account_id,SessionId={{SessionId}})';
const getGoogleAdGroupsUrlTmpl =
  '/ImportSession/GoogleAdGroups(CustomerId=:customer_id,AccountId=:account_id,SessionId={{SessionId}})';
const getGoogleAccountPropertiesUrlTmpl =
  '/ImportSession/GoogleAccountProperties(CustomerId=:customer_id,AccountId=:account_id,SessionId={{SessionId}})';
const getMultiGoogleAccountPropertiesUrlTmpl =
  '/ImportSession/MultiGoogleAccountProperties(CustomerId=:customer_id,AccountId={{AccountId}},SessionId={{SessionId}})';
const importType = 'GoogleAPIImport';
const keyNameOfSessionIdInCookie = 'OAuth2_AdWordsSessionId';
const keyNameOfLoginAccountInCookie = 'OAuth2_GoogleLoginAccount';
const keyNameOfGMCEmailAddressInCookie = 'OAuth2_GMCLoginEmailAddress';
const googleEntitiesBulkFileTypeNum = 17;
const googleAdsEntityBulkFileType = 'GoogleAdsEntityIdFile';

export class GoogleImportRepository extends CommonImportRepository {
  constructor({ deps }) {
    super({ deps: deps || {}, type: 'GoogleImport', importType });

    if (deps != null) {
      this.edm = deps.edm;
      this.jsData = deps.jsData;
      this.isMultiAccountImport = deps.isMultiAccountImport;
      if (this.edm != null) {
        this.taskItemOptions = {
          adapter: 'odata',
          entitySet: this.isMultiAccountImport ?
            this.edm.currentCustomer.TaskItems : this.edm.currentAccount.TaskItems,
        };
      }
    }
  }

  startGoogleSession(authCode, shareWithUserIds = null) {
    let url = getGoogleSessionUrlTmpl.replace('{{AuthCode}}', authCode);

    if (!_.isEmpty(shareWithUserIds)) {
      url = `${url}&shareWithUserIds=${shareWithUserIds}`;
    }

    if (this.newRepositoryEnabled) {
      url = url.replace(':account_id', this.aid);
    }

    return this.startGoogleSessionInternal(url);
  }

  startGoogleSessionWithToken(token, shareWithUserIds = null) {
    let url = getGoogleSessionWithTokenUrlTmpl.replace('{{Token}}', token);

    if (this.newRepositoryEnabled) {
      url = url.replace(':account_id', this.aid);
    }

    if (!_.isEmpty(shareWithUserIds)) {
      url = `${url}&shareWithUserIds=${shareWithUserIds}`;
    }

    return this.startGoogleSessionInternal(url);
  }

  startGoogleSessionInternal(url) {
    return this.odata.post(url, { data: '' })
      .then((sessionId) => {
        if (sessionId) {
          return this.fetchGoogleLoginEmail(sessionId)
            .then((loginAccount) => {
              this.setGoogleImportAccountInfoInCookie({
                sessionId,
                loginAccount,
              });
              return sessionId;
            });
        }
        return sessionId;
      });
  }

  fetchGoogleLoginEmail(sessionId) {
    let url = getGoogleLoginEmailUrlTmpl.replace('{{SessionId}}', sessionId);

    if (this.newRepositoryEnabled) {
      url = url.replace(':account_id', this.aid);
    }

    return this.odata.get(url);
  }

  getGoogleCampaigns(googleCustomerId) {
    const sessionId = this.getGoogleSessionIdFromCookie();
    let url = getGoogleCampaignsUrlTmpl.replace('{{SessionId}}', sessionId);
    if (this.newRepositoryEnabled) {
      url = url.replace(':account_id', this.aid);
    }
    url = this.formatUrl({
      url,
      queryParameters: {
        googleCustomerId,
      },
    });

    return this.getGoogleCampaignsInternal(url);
  }

  getGoogleCampaignsInternal(url) {
    return this.odata.get(url)
      .then(data => filterAndLocalizeCampaignsUtil(data, this.i18n, this.logError));
  }

  getGoogleAdGroupsByCampaignId(googleCustomerId, googleCampaignId, usecache = true) {
    const sessionId = this.getGoogleSessionIdFromCookie();
    let url = getGoogleAdGroupsUrlTmpl.replace('{{SessionId}}', sessionId);
    if (this.newRepositoryEnabled) {
      url = url.replace(':account_id', this.aid);
    }
    url = this.formatUrl({
      url,
      queryParameters: {
        googleCustomerId,
        campaignId: googleCampaignId,
        usecache,
      },
    });

    return this.odata.get(url);
  }

  getGoogleAdGroupsByBlobName(googleCustomerId, blobName, usecache = true) {
    const sessionId = this.getGoogleSessionIdFromCookie();
    let url = getGoogleAdGroupsUrlTmpl.replace('{{SessionId}}', sessionId);
    if (this.newRepositoryEnabled) {
      url = url.replace(':account_id', this.aid);
    }
    url = this.formatUrl({
      url,
      queryParameters: {
        googleCustomerId,
        blobName,
        usecache,
      },
    });

    return this.odata.get(url);
  }

  containDisplayType(campaignsList) {
    return containDisplayTypeUtil(campaignsList, this.i18n);
  }

  getLocalizedCampaignTypeString(type) {
    return getLocalizedCampaignTypeStringUtil(type, this.i18n);
  }

  getGoogleAccounts(currentSessionId = null, withCamapignCount = false) {
    const sessionId = currentSessionId || this.getGoogleSessionIdFromCookie();
    let url = getGoogleAccountsUrlTmpl.replace('{{SessionId}}', sessionId);
    if (this.newRepositoryEnabled) {
      url = url.replace(':account_id', this.aid);
    }
    if (withCamapignCount) {
      url = `${url}?version=V2`;
    }

    return this.odata.get(url);
  }

  getGoogleSessionIdFromCookie() {
    this.logTrace('start getGoogleSessionIdFromCookie');
    return this.getValueInSubImportCookieByAccountId(keyNameOfSessionIdInCookie)[0];
  }

  getGoogleLoginAccountFromCookie() {
    this.logTrace('start getGoogleLoginAccountFromCookie');
    return this.getValueInSubImportCookieByAccountId(keyNameOfLoginAccountInCookie)[0];
  }

  // this is for fetching multiple items from cookie
  getGoogleImportInfoFromCookie() {
    this.logTrace('start getGoogleImportInfoFromCookie');
    const result = this.getValueInSubImportCookieByAccountId(
      keyNameOfSessionIdInCookie,
      keyNameOfLoginAccountInCookie
    );

    return {
      googleSessionId: result[0],
      googleAccountName: result[1],
    };
  }

  isUserLoggedIn() {
    return !!this.getGoogleSessionIdFromCookie();
  }

  getGoogleAccountName() {
    return this.getGoogleLoginAccountFromCookie();
  }

  setGoogleImportLoginAccountInCookie(loginAccount) {
    this.addSubCookieToImportCookie({
      [keyNameOfLoginAccountInCookie]: loginAccount,
    });
  }

  setGoogleImportAccountInfoInCookie({ loginAccount, sessionId }) {
    this.addSubCookieToImportCookie({
      [keyNameOfLoginAccountInCookie]: loginAccount,
      [keyNameOfSessionIdInCookie]: sessionId,
    });
  }

  serializeGoogleEntitiesSelectionDataToStringPayload(selectionData) {
    return new StringPayload({
      content: JSON.stringify({
        CampaignIds: selectionData.selectedCampaignIds,
        AdGroupIdMap: selectionData.selectedAdGroupMap,
        CampaignIdsWithActiveAdGroups: selectionData.selectedWithActiveOnly,
      }),
    });
  }

  convertAdGroupDataToIdMap(adGroupData) {
    return _.mapObject(_.groupBy(adGroupData, 'campaignId'), arr => _.pluck(arr, 'Id'));
  }

  getCleanedGoogleAdGroupSelectionDataFromBlob(customerId, blobName) {
    return this.getGoogleAdGroupsByBlobName(customerId, blobName)
      .then(adGroupData => this.convertAdGroupDataToIdMap(adGroupData));
  }

  getSelectionDataFromBlob(blobName) {
    return this.getEntitiesBlob(blobName)
      .then(blob =>
        ({
          selectedCampaignIds: blob.CampaignIds,
          selectedAdGroupMap: blob.AdGroupIdMap,
          selectedWithActiveOnly: blob.CampaignIdsWithActiveAdGroups,
        }));
  }

  getEntitiesBlob(blobName) {
    return this.getBulkBlob(blobName, googleEntitiesBulkFileTypeNum);
  }

  // add clean up campaigns in here
  prepareForCampaignSelectionStep(customerId, blobName) {
    const promises = {};
    promises.googleCampaigns = this.getGoogleCampaigns(customerId);

    if (blobName) {
      promises.selectionData = this.getSelectionDataFromBlob(blobName)
        .then((selectionData) => {
          const finalState = {};
          finalState.selectedCampaignIds = selectionData.selectedCampaignIds;
          finalState.selectedWithActiveOnly = selectionData.selectedWithActiveOnly;

          if (!_.isEmpty(selectionData.selectedAdGroupMap)) {
            return this.getCleanedGoogleAdGroupSelectionDataFromBlob(customerId, blobName)
              .then((selectedAdGroupMap) => {
                finalState.selectedAdGroupMap = selectedAdGroupMap;
                return finalState;
              });
          }

          finalState.selectedAdGroupMap = {};
          return finalState;
        });
    }

    return Promise.props(promises)
      .then(({ googleCampaigns, selectionData }) => {
        const finalState = { googleCampaigns };
        if (selectionData) {
          const availableIdBoolMap = _.object(_.map(googleCampaigns, obj => [obj.Id, true]));
          const campaignIdFilter = ids => _.filter(ids, id => availableIdBoolMap[id]);
          const selectedCampaignIds = campaignIdFilter(selectionData.selectedCampaignIds);
          const selectedWithActiveOnly = campaignIdFilter(selectionData.selectedWithActiveOnly);
          // All data will be checked and set to null if it is empty.
          // It will resave existing wrong blob files like "{ AdGroupIdMap:{} }""
          // which saved at smart schedule step when editing expert tasks before.
          // https://portal.microsofticm.com/imp/v3/incidents/details/303083319/home
          _.extend(finalState, {
            selectedCampaignIds: _.isEmpty(selectedCampaignIds) ? null : selectedCampaignIds,
            selectedWithActiveOnly: _.isEmpty(selectedWithActiveOnly)
              ? null : selectedWithActiveOnly,
            selectedAdGroupMap: _.isEmpty(selectionData.selectedAdGroupMap) ?
              null : selectionData.selectedAdGroupMap,
          });
        }
        return finalState;
      });
  }

  prepareAdWordsImportContext(
    googleCustomerId,
    campaignIds,
    initialAdWordsAccount,
    loginCustomerId,
    googleEntitiesSelectionData,
    uid
  ) {
    let sessionId;
    let googleAccountName;

    if (initialAdWordsAccount) {
      ({ sessionId } = initialAdWordsAccount);
      ({ googleAccountName } = initialAdWordsAccount);
    } else {
      this.logTrace('prepareAdWordsImportContext: start to getGoogleImportInfoFromCookie');
      const info = this.getGoogleImportInfoFromCookie();

      sessionId = info.googleSessionId;
      ({ googleAccountName } = info);

      if (!sessionId) {
        this.logTrace(`prepareAdWordsImportContext: failed to getGoogleImportInfoFromCookie with info ${JSON.stringify(info)}`);
        sessionId = this.getGoogleSessionIdFromCookie();
        googleAccountName = this.getGoogleLoginAccountFromCookie();

        if (!sessionId) {
          this.logTrace(`prepareAdWordsImportContext: failed to getGoogleSessionIdFromCookie with sessionId ${sessionId} googleAccountName ${googleAccountName}`);
        }
      }
    }

    const adWordsImportContext = {
      '@odata.type': '#Model.AdWordsImportContext',
      SessionId: sessionId,
      CustomerId: parseInt(googleCustomerId, BASE_10_RADIX),
      AccountName: googleAccountName,
      LoginCustomerId: loginCustomerId,
      CampaignIds: campaignIds,
      AdWordsSessionUserId: uid,
    };

    if (googleEntitiesSelectionData) {
      return this.uploadToAzureBlobViaOdata({
        bulkFileType: googleAdsEntityBulkFileType,
        payload:
          this.serializeGoogleEntitiesSelectionDataToStringPayload(googleEntitiesSelectionData),
      }).then(blob =>
        ({
          ...adWordsImportContext,
          GoogleEntitiesBlobName: blob.BlobName,
        }));
    }

    return Promise.resolve(adWordsImportContext);
  }

  prepareGoogleImportTaskItem({
    importOptions,
    stepState,
  }) {
    const {
      importUserPreferences,
      notificationType,
      notificationEmailAddresses,
      timeZoneId,
      name,
      cron,
      taskItemState,
      ScenarioIdentifier,
      DefaultImportSettings,
    } = importOptions;

    const {
      googleCustomerId,
      campaignIds,
      initialAdWordsAccount,
      loginCustomerId,
      googleEntitiesSelectionData,
    } = stepState;

    return this.prepareAdWordsImportContext(
      googleCustomerId, campaignIds, initialAdWordsAccount,
      loginCustomerId, googleEntitiesSelectionData, this.uid
    )
      .then(adWordsImportContext =>
        (_.extend(
          this.prepareCompleteImportTaskItem(importUserPreferences, timeZoneId), {
            NotificationType: notificationType,
            NotificationEmailAddresses: notificationEmailAddresses,
            AdWordsImportContext: adWordsImportContext,
            ImportType: importType,
            ImportAction: null,
            FileImportContext: null,
          },
          // pass IsAutoFrequency and IsSmartImport if the properties exist
          _.pick(importOptions, 'IsAutoFrequency', 'IsSmartImport', 'IsSmartCampaignOnly', 'IsEscImport'),
          !!name && { Name: name },
          !!taskItemState && { State: taskItemState },
          !!cron && { Cron: cron },
          !!DefaultImportSettings && { DefaultImportSettings },
          !!ScenarioIdentifier && { ScenarioIdentifier }
        )));
  }

  setInProcessImportCookie({ importId }) {
    super.setInProcessImportCookie({
      importId,
      importType,
    });
  }

  getGoogleImportUserPreferences() {
    const preferences = {};

    return this.getImportUserPreferencesWithBMCandMinInfo(importType)
      .then(userPref => _.extend(preferences, userPref));
  }

  mergeGoogleAccountProperties(preferences, googleAccountProperties) {
    if (!preferences) {
      return preferences;
    }
    if (googleAccountProperties === false) {
      return false;
    }
    const { TrackingTemplate, FinalUrlSuffix } = googleAccountProperties;

    // eslint-disable-next-line one-var
    const UserPreferenceTrackingTemplate =
      preferences.AdWordsAccountTrackingTemplate || preferences.AdWordsAccountFinalUrlSuffix;

    return _.extend(
      preferences,
      {
        UserPreferenceTrackingTemplate,
        TrackingTemplateValue: TrackingTemplate,
        FinalUrlSuffixValue: FinalUrlSuffix,
        AdWordsAccountTrackingTemplate: TrackingTemplate,
        AdWordsAccountFinalUrlSuffix: FinalUrlSuffix,
      }
    );
  }

  getGoogleAccountProperties(googleCustomerId) {
    const sessionId = this.getGoogleSessionIdFromCookie();

    if (sessionId) {
      const urlTemplate = getGoogleAccountPropertiesUrlTmpl;
      let url = urlTemplate.replace('{{SessionId}}', sessionId);
      if (this.newRepositoryEnabled) {
        url = url.replace(':account_id', this.aid);
      }

      url = this.formatUrl({
        url,
        queryParameters: {
          googleCustomerId,
        },
      });

      return this.odata.get(url);
    }

    this.logTrace('Did not send request in method getGoogleAccountProperties because no SessionId was found');

    return Promise.resolve(false);
  }

  getMultiAccountGoogleImportUserPreferences(googleCustomerList) {
    let preferences = {};

    return this.getMultiAccountImportUserPreferenceWithBMCandMinInfo()
      .then((userPref) => {
        preferences = _.extend(preferences, userPref);
        const googleCustomerIdList = _.keys(googleCustomerList);
        return this.getMultiAccountGoogleAccountProperties(googleCustomerIdList.join());
      })
      .then((result) => {
        if (result === false) {
          return false;
        }
        const TrackingTemplate = [];
        const FinalUrlSuffix = [];
        _.each(googleCustomerList, (item) => {
          if (result[item.CustomerId]) {
            TrackingTemplate.push({
              googleCustomerId: item.CustomerId,
              googleAccountName: item.AccountName,
              trackingTemplate: result[item.CustomerId].TrackingTemplate,
            });
            FinalUrlSuffix.push({
              googleCustomerId: item.CustomerId,
              googleAccountName: item.AccountName,
              finalUrlSuffix: result[item.CustomerId].FinalUrlSuffix,
            });
          }
        });

        preferences = _.extend(
          preferences,
          {
            AdWordsAccountTrackingTemplate: TrackingTemplate,
            AdWordsAccountFinalUrlSuffix: FinalUrlSuffix,
          }
        );
        return preferences;
      });
  }

  getMultiAccountGoogleAccountProperties(googleCustomerIdList) {
    const sessionId = this.getGoogleSessionIdFromCookie();

    if (sessionId) {
      let url = _.template(getMultiGoogleAccountPropertiesUrlTmpl)({
        AccountId: this.aid,
        SessionId: sessionId,
      });

      url = this.formatUrl({
        url,
        queryParameters: {
          googleCustomerIdList,
        },
      });

      return this.odata.get(url);
    }

    this.logTrace('Did not send request in method getMultiAccountGoogleAccountProperties because no SessionId was found');

    return Promise.resolve(false);
  }

  logout() {
    // When logging out of Google Ads, remove the Google Ads cookies and the
    // Google Merchant Center cookie. The GMC cookie is from the Merchant Center Mapping Step.
    this.removeImportCookieValue(
      keyNameOfSessionIdInCookie,
      keyNameOfLoginAccountInCookie,
      keyNameOfGMCEmailAddressInCookie
    );

    window.frames.logoutIFrame.location = GOOGLE_LOGOUT_URL;
  }

  checkOpportunityType() {
    const { opportunityType } = this.deps;

    return opportunityType === 'first' || opportunityType === 'reimport' || opportunityType === 'GoogleImportOpportunity' || opportunityType === 'GoogleImportScheduledOpportunity';
  }
}
